import React, { useEffect, useMemo, useState } from "react";
import { useTheme } from "@mui/material/styles";
import Header from "../../layout/header";
import { useNotificationService } from "../../hooks/notification";
import { useQuery } from "@tanstack/react-query";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { apiGetPost, apiGetRepresentative } from "../../api/home";
import { BackArrowIcon } from "../../components/ui-elements/svg-icons";
import SearchContent from "../../components/search/search-content";
import { useLocation } from "react-router-dom";
import {
  dummySearchResultPropsData,
  SearchProps,
  SearchPropsDummy,
  SearchResultPropsData,
} from "../../interface/search";
import { PATH_DASHBOARD } from "../../routes/path";
import {
  SkeletonAccountLoading,
  SkeletonLoading,
} from "../../components/ui-elements/skeleton-loading";
import useResponsive from "../../hooks/useResponsive";
import { PostApiProp, RepresentativeApiProp } from "../../interface/api";

const SearchPage = () => {
  const theme = useTheme();
  const location = useLocation();
  const search = location?.state?.search ?? "";
  const state = location?.state?.state ?? "";
  const lga = location?.state?.lga ?? "";
  const lga_name = location?.state?.lga_name ?? "";
  const reps_id = location?.state?.reps_id ?? "";
  const reps_name = location?.state?.reps_name ?? "";
  const isSmall = useResponsive("down", "sm", "xl");

  const { showNotification } = useNotificationService();
  const navigate = useNavigate();
  const [path, setPath] = useState({
    page: 1,
    search: search ?? "",
    page_size: 10,
    state: state ?? "",
    local_government: lga ?? "",
    representative_id: reps_id ?? "",
  });

  const [results_info, setResults_info] = useState<SearchResultPropsData>(
    dummySearchResultPropsData
  );

  const searchRepsParams: RepresentativeApiProp = useMemo(
    () => ({
      search: location?.state?.search ?? "",
      state: location?.state?.state ?? "",
      local_government: location?.state?.lga ?? "",
      position: "",
      page: 1,
      page_size: 10,
      sort_by: "",
      sort_order: "asc",
    }),
    [location?.state]
  );
  const searchPostParams: PostApiProp = useMemo(
    () => ({
      search: location?.state?.search ?? "",
      status: "",
      category: "",
      page: 1,
      page_size: 10,
      sort_by: "",
      sort_order: "asc",
    }),
    [location.state]
  );

  const fetchFunction = () => {
    if (location?.state?.resp === "true")
      return apiGetRepresentative(searchRepsParams);
    else if (location?.state?.resp === "false")
      return apiGetPost(searchPostParams);
  };

  const fetchParams =
    location?.state.resp === "true" ? searchRepsParams : searchPostParams;

  const { data, error, isLoading } = useQuery({
    queryKey: ["searchResults", fetchParams, location?.state?.resp],
    queryFn: () => fetchFunction(),
    enabled: location?.state.resp === "true" || !!location?.state.search, // Runs if resp is "true" OR search exists
    select: (response) => {
      if (response?.status === 200) {
        return response?.data;
      }
      return (
        response?.data || {
          data: [],
          meta: {
            total: 0,
            current_page: 1,
            last_page: 1,
            page_size: 10,
          },
        }
      );
    },
  });

  return (
    <>
      {/* Header Menu */}
      <Box
        sx={{
          background: theme.palette.background.paper,
        }}
      >
        <Header>
          <Stack direction={"row"} spacing={1} alignItems={"center"}>
            <Button onClick={() => navigate(PATH_DASHBOARD.root)}>
              <BackArrowIcon />
            </Button>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              Search Result
            </Typography>
          </Stack>
        </Header>
        {isLoading && (
          <Box
            sx={{
              width: isSmall ? "90%" : "60%",
              height: "90vh",
              pt: "6rem",
              mx: "auto",
            }}
          >
            {[...Array(3)].map((_, idx) => (
              <SkeletonLoading key={idx} />
            ))}
          </Box>
        )}
        {!isLoading && (
          <SearchContent
            search_data={data}
            search={search}
            state={state}
            lga={lga_name}
            resp={location?.state?.resp}
            reps_name={reps_name}
          />
        )}
      </Box>
    </>
  );
};

export default SearchPage;
